/**
 * V325.29: Keep bottom-tab pages mounted to prevent img remount churn.
 * Renders all visited tab pages simultaneously, hiding inactive ones with display:none.
 * Prevents React Router from unmounting pages on tab switch, avoiding <img> recreation.
 */
import React, { useState, useEffect, Suspense } from 'react';
import { useLocation, Routes, Route } from 'react-router-dom';
import { trackEvent, AnalyticsEvents, type ScreenName } from '@/analytics';

const BOTTOM_TAB_PATHS = ['/discover', '/network', '/chat', '/profile', '/settings'] as const;
type TabPath = (typeof BOTTOM_TAB_PATHS)[number];

function isTabPath(pathname: string): pathname is TabPath {
  if (pathname === '/discover' || pathname === '/network' || pathname === '/profile' || pathname === '/settings') return true;
  if (pathname === '/chat') return true;
  return false;
}

function getTabKey(pathname: string): string {
  if (pathname.startsWith('/chat/')) return 'chat-conversation';
  if (isTabPath(pathname)) return pathname;
  return '';
}

/** V208.0: Map path to hardcoded screen enum — never derive from URL */
function pathToScreen(pathname: string): ScreenName | null {
  if (pathname === '/discover') return 'Discover';
  if (pathname === '/network') return 'Network';
  if (pathname === '/profile') return 'Profile';
  if (pathname === '/settings') return 'Settings';
  if (pathname === '/chat' || pathname.startsWith('/chat/')) return 'Chat';
  return null;
}

interface KeepAliveTabsProps {
  fallback: React.ReactNode;
  Discover: React.LazyExoticComponent<React.ComponentType<any>>;
  Network: React.LazyExoticComponent<React.ComponentType<any>>;
  ChatList: React.LazyExoticComponent<React.ComponentType<any>>;
  ChatConversation: React.LazyExoticComponent<React.ComponentType<any>>;
  Profile: React.LazyExoticComponent<React.ComponentType<any>>;
  Settings: React.LazyExoticComponent<React.ComponentType<any>>;
}

export function KeepAliveTabs({
  fallback,
  Discover,
  Network,
  ChatList,
  ChatConversation,
  Profile,
  Settings,
}: KeepAliveTabsProps): React.ReactElement {
  const location = useLocation();
  const pathname = location.pathname;

  const [mounted, setMounted] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    if (isTabPath(pathname)) initial[pathname] = true;
    if (pathname.startsWith('/chat/')) initial['chat-conversation'] = true;
    return initial;
  });

  useEffect(() => {
    const key = getTabKey(pathname);
    if (key) {
      setMounted((m) => (m[key] ? m : { ...m, [key]: true }));
    }
    const screen = pathToScreen(pathname);
    if (screen) {
      trackEvent(AnalyticsEvents.SCREEN_VIEWED, { screen }).catch(() => { });
    }
  }, [pathname]);

  const isChatConversation = pathname.startsWith('/chat/') && pathname !== '/chat';
  const activeKey = isChatConversation ? 'chat-conversation' : (pathname as TabPath);

  const renderTab = (key: string, visible: boolean) => {
    // V342: Default overflow hidden — inner page scroll containers own scroll (iOS: no competing UIScrollView).
    // discover/profile/settings merge overflow:hidden; network/chat use same base (inner refs scroll).
    const style: React.CSSProperties = {
      display: visible ? 'block' : 'none',
      height: '100%',
      minHeight: 0,
      overflow: 'hidden',
    };
    switch (key) {
      case '/discover':
        return (
          <div key={key} style={{ ...style, overflow: 'hidden' }} data-keepalive-tab="discover">
            <Suspense fallback={fallback}><Discover /></Suspense>
          </div>
        );
      case '/network':
        return (
          <div key={key} style={style} data-keepalive-tab="network">
            <Suspense fallback={fallback}><Network /></Suspense>
          </div>
        );
      case '/chat':
        return (
          <div
            key={key}
            style={{
              ...style,
              display: visible ? 'flex' : 'none',
              flexDirection: 'column',
              width: '100%',
              flex: '1 1 0%',
            }}
            data-keepalive-tab="chat"
          >
            <Suspense fallback={fallback}><ChatList /></Suspense>
          </div>
        );
      case 'chat-conversation':
        return (
          <div key={key} style={style} data-keepalive-tab="chat-conversation">
            <Routes>
              <Route path="/chat/:chatId" element={<Suspense fallback={fallback}><ChatConversation /></Suspense>} />
            </Routes>
          </div>
        );
      case '/profile':
        return (
          <div key={key} style={{ ...style, overflow: 'hidden' }} className="profile-tab-scroll" data-keepalive-tab="profile">
            <Suspense fallback={fallback}><Profile /></Suspense>
          </div>
        );
      case '/settings':
        return (
          <div key={key} style={{ ...style, overflow: 'hidden' }} data-keepalive-tab="settings">
            <Suspense fallback={fallback}><Settings /></Suspense>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      {Object.entries(mounted).map(([key, isMounted]) =>
        isMounted ? renderTab(key, key === activeKey) : null
      )}
    </div>
  );
}
